<template>
    <div class="watchlist-info">
        <div class="watchlist-info__container">
            <TotemToast ref="toast" />
            <div class="watchlist-info__durations" v-if="areThereItemsInStudyPlans">
                <span class="icon-clock watchlist-info__durations-icon"></span>
                <span class="watchlist-info__durations-amount">
                    <span v-html="$t('WatchListCourses.totalduration')"></span>: {{ watchlistTotalDuration }} {{ $t("CompletedTotalTime.hours")}}
                </span>
            </div>
            <div class="watchlist-info__inputs">
                <template v-if="isMobileView">
                    <TotemButton color="secondary" @click="editData" :width="200" v-if="!editable" :label="$t('WatchListControls.edit')" />
                    <TotemButton color="secondary" @click="saveData" :width="200" v-if="editable" :label="$t('WatchListControls.save')" />
                </template>
                <div class="watchlist-info__title-container">
                    <TotemInput v-model="editedWatchList.title" :disabled="!editable" :label="$t('WatchListControls.study_plan_name')" fullWidth />
                    <template v-if="isDesktopView && !watchlist.isReadonly" >
                        <TotemButton color="secondary" @click="editData" v-if="!editable" :label="$t('WatchListControls.edit')" />
                        <TotemButton color="secondary" @click="saveData" v-if="editable" :label="$t('WatchListControls.save')" />
                    </template>
                </div>
                <div class="watchlist-info__dates">
                    <div class="watchlist-info__datepicker-container">
                        <TotemDatepicker
                            :label="$t('General.start_date')"
                            v-model="editedWatchList.from"
                            :disabled="!editable"
                            format='yyyy-MM-dd'
                            :width="285"
                        />
                        <TotemDatepicker
                            :label="$t('General.end_date')"
                            v-model="editedWatchList.to"
                            :disabled="!editable"
                            format='yyyy-MM-dd'
                            :width="285"
                        />
                        <TotemButton
                            @click="emptyDateRange"
                            v-if="showClearDatesButton && !watchlist.isReadonly"
                            :disabled="!editable"
                            class="watchlist-info__empty-dates"
                            :label="$t('WatchListControls.clear_fields')"
                        />
                    </div>
                </div>
            </div>
            <div v-if="!watchlist.isReadonly" class="watchlist-info__buttons">
                <div class="watchlist-info__button-remove">
                    <span
                        v-if="!editable"
                        class="watchlist-info__button-remove-info"
                        v-tooltip="{ content: $t('WatchListControls.remove_button_info'), html: true }"
                    >
                    </span>
                </div>
            </div>
        </div>

        <div class="watchlist-info__description">
            <TotemEditor :editable="editable" v-model="editedWatchList.note" :content="originalNote" :height="147" ref="noteEditor" class="watchlist-info__editor"/>
        </div>
        <p class="watchlist-info__courses-in-watchlist" v-html="$t('WatchListControls.courses_in_watchlist')"></p>
        <p class="watchlist-info__order-info" v-html="showChangeOrderInfo"></p>

        <div class="watchlist-info__toggle">
            <TotemToggleButton
                v-model="showExpiredStudies"
                :label="$t('CourseListFilter.show_expired_courses')"
                v-on:input="$emit('toggleExpiredStudies', showExpiredStudies)"
                class="watchlist-info__toggle-button"
            />
            <TotemCompletedCheckbox v-model="showCompleted" :editable="editable" />
            <TotemButton
                v-if="!watchlist.isReadonly"
                color="primary"
                :disabled="!editable"
                @click="removeElements"
                :label="$t('WatchListControls.remove_courses')"
            />
        </div>
    </div>
</template>
<script>
    import dateHelper from 'components/shared/mixins/dateHelper'

    export default {
        name: 'WatchListControllsAndDetails',
        mixins: [ dateHelper ],
        data () {
            return {
                editedWatchList: {
                    title: null,
                    from: '',
                    to: '',
                    note: null,
                    isHidden: false
                },
                originalNote: null,
                showExpiredStudies: true
            }
        },
        props: {
            watchlist: Object,
            editable: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            showClearDatesButton () {
                return this.editedWatchList.from !== '' && this.editedWatchList.to !== ''
            },
            showChangeOrderInfo () {
                return this.editable ? this.$t('WatchListControls.drag_drop_info') : this.$t('WatchListControls.change_order_info')
            },
            sumCourseDurations () {
                let courseDurations = 0
                this.watchlist.courses.forEach((course, index) => {
                    courseDurations += course.videoDuration
                })
                let sumOfCourseDurations = (courseDurations / 60).toFixed(2)
                return sumOfCourseDurations
            },
            sumCollectionDurations () {
                let collectionDurations = 0
                this.watchlist.collections.forEach((collection) => {
                    collectionDurations += collection.coursesTotalDuration
                })
                let sumOfCollectionDurations = (collectionDurations / 60).toFixed(2)
                return sumOfCollectionDurations
            },
            sumLearningPathDurations () {
                let learningPathDurations = 0
                this.watchlist.learningPaths.forEach((learningPath) => {
                    learningPathDurations += learningPath.coursesTotalDuration
                })
                let sumOfLearningPathDurations = (learningPathDurations / 60).toFixed(2)
                return sumOfLearningPathDurations
            },
            watchlistTotalDuration () {
                return (+this.sumCourseDurations + +this.sumCollectionDurations + +this.sumLearningPathDurations).toFixed(2)
            },
            areThereItemsInStudyPlans () {
                return this.watchlist.courses.length > 0 || this.watchlist.collections.length > 0 || this.watchlist.learningPaths.length > 0
            }
        },
        methods: {
            async onSaveData () {
                this.showSaveToast()
            },
            editData () {
                this.$emit('editData')
            },
            saveData () {
                this.$emit('saveData')
            },
            removeElements () {
                this.$emit('removeElements')
            },
            emptyDateRange () {
                this.editedWatchList.from = ''
                this.editedWatchList.to = ''
            },
            onWatchListChange (newWatchList) {
                if (newWatchList !== undefined) {
                    this.editedWatchList.title = newWatchList.title
                    this.originalNote = newWatchList.note
                    this.editedWatchList.from = this.formatQueryDateToDate(newWatchList.from)
                    this.editedWatchList.to = this.formatQueryDateToDate(newWatchList.to)
                }
            },
            getElementsToSave () {
                return this.editedWatchList
            },
            showSaveToast () {
                this.$refs.toast.show(this.$t('WatchListControls.watchlist_saved'))
            }
        },
        watch: {
            watchlist: {
                handler: 'onWatchListChange',
                immediate: true
            },
            showCompleted () {
                this.$emit('input', this.showCompleted)
            }
        }
    }
</script>

<style lang="scss" scoped>
    .watchlist-info {
        &__container {
            display: flex;
            margin: 0 0 10px;
            flex-direction: column;

            [class*="desktop_x"] & {
                flex-direction: column;
                margin: 0 0 20px;
            }
        }

        &__subheader {
            position: relative;
            font-size: 2.3rem;
            display: flex;
            align-items: center;
            margin: 10px 0;
        }

        .icon-info {
            margin: 0 0 0 12px;
        }

        &__toggle {
            display: flex;
            gap: 10px;
            padding: 20px 0 0;
            align-items: center;

            [class*="mobile"] & {
                flex-direction: column;
                align-items: flex-start;
            }
        }

        &__toggle-button {
            [class*=mobile] & {
                width: auto !important;
            }
        }

        &__courses-in-watchlist {
            margin: 20px 0 0;
            font-family: 'PlusJakartaSans-Medium', sans-serif;
            font-size: 2rem;
            overflow-wrap: break-word;
        }

        &__order-info {
            font-family: 'PlusJakartaSans-Medium', sans-serif;
            line-height: 150%;
            font-size: 1.6rem;
            padding: 20px 0 0;
        }

        &__durations {
            flex-direction: row;
            align-items: center;
            overflow: hidden;
            height: 44px;
            padding: 10px 20px;
            display: inline-flex;
            border: solid 1px $lavender;
            background: $white;
            border-radius: 5px;
            width: max-content;
            margin: 0 0 20px;
        }

        &__durations-icon {
          margin: 0 8px 0 0;
          font-size: 2rem;
          color: $lavender;
        }

        &__durations-amount {
            font-size: 1.5rem;
        }

        &__duration {
          margin: 10px 5px 0 0;
          font-size: 1.7rem;
        }

        &__inputs {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            gap: 20px;
        }

        &__title-container {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 20px;
        }

        &__dates {
            display: flex;
            flex-direction: column;
        }

        &__dates-label {
            padding: 0 0 10px 0;

            [class*="desktop"] & {
                padding: 0 10px 0 0;
            }
        }

        &__datepicker-container {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 10px;

            [class*="mobile"] & {
                flex-direction: column;
                align-items: stretch;
            }
        }

        &__editor {
            border: 1px solid $lavender;
        }

        &__hide-watchlist {
          flex-direction: row;
          align-items: center;
          display: flex;
          margin: -5px 25px 0 0;
        }

        &__hide-watchlist-label {
          margin: 0 10px 0 0;
        }

        &__empty-dates {
            align-self: flex-start;
            margin: 3px 0 0;

            [class*="mobile_sm"] & {
                height: 45px !important;
            }
        }

        &__buttons {
            display: flex;
            flex-direction: column;
            align-self: flex-start;

            > button {
                align-self: flex-start;
                margin: 0 0 10px;
            }

            [class*="desktop"] & {
                flex-direction: row;
                > button {
                    margin: 0 10px 0 0;
                }
            }

            [class*="desktop_x"] & {
                > button {
                    margin: 0 0 0 10px
                }
            }
        }

        &__button-remove {
            position: relative;
            [class*="mobile"] & {
                margin: 0 0 10px 0;
            }
        }

        &__button-remove-info {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
        }

        &__study-plan-title {
            [class*="mobile"] & {
                margin: 10px 0 10px 0;
            }
        }
    }
</style>
